import { MolecularWorkspace } from '@alchemiesolns/molecular-workspace';
import { VisibilityFlag } from '@alchemiesolns/visibility-config';
import { buildIndexMaps, fromAtomContainer } from './atomContainer';
import { AtomContainer, AtomModel, BondModel, AtomChirality } from '@alchemiesolns/atomcontainer';
import { stableId } from '@alchemiesolns/stable-id';
import type { ArrowIdDef } from '@alchemiesolns/arrow-def';
import type {
  WorkspaceSnapshotV1,
  SnapshotAtom,
  SnapshotBond,
  SnapshotArrow,
  SnapshotHydrogenBond,
  SnapshotAnnotation,
  SnapshotGroup,
  SnapshotTemplate,
  SnapshotGroupInstance,
  SnapshotInstanceOverrides,
} from './types';

/**
 * Convert a MolecularWorkspace to a WorkspaceSnapshotV1.
 *
 * Index references in the snapshot are self-referential — relative to the arrays
 * in this snapshot, based on Map iteration order at serialization time.
 */
export function toSnapshot(workspace: MolecularWorkspace): WorkspaceSnapshotV1 {
  const { atomIdToIndex, bondIdToIndex, atomIds } = buildIndexMaps(workspace);

  // Atoms
  const atoms: SnapshotAtom[] = [];
  for (const atom of workspace.getAllAtoms()) {
    const entry: SnapshotAtom = { symbol: atom.symbol, x: atom.x, y: atom.y };
    if (atom.charge !== 0) entry.charge = atom.charge;
    if (atom.lonePairs !== 0) entry.lonePairs = atom.lonePairs;
    if (atom.radicals !== 0) entry.radicals = atom.radicals;
    if (atom.chirality) entry.chirality = atom.chirality;
    atoms.push(entry);
  }

  // Bonds
  const bonds: SnapshotBond[] = [];
  for (const bond of workspace.getAllBonds()) {
    const a1 = atomIdToIndex.get(bond.a1.id);
    const a2 = atomIdToIndex.get(bond.a2.id);
    if (a1 === undefined || a2 === undefined) continue;

    const entry: SnapshotBond = { a1, a2, order: bond.order };
    if (bond.bondType !== 'l') entry.type = bond.bondType;
    if (bond.isomerConfig) entry.isomerConfig = bond.isomerConfig;
    bonds.push(entry);
  }

  const snapshot: WorkspaceSnapshotV1 = { v: 1, atoms, bonds };

  // Arrows (optional)
  const wsArrows = workspace.getArrows();
  if (wsArrows.length > 0) {
    const arrows: SnapshotArrow[] = [];
    for (const arrow of wsArrows) {
      const srcIsAtom = arrow.type === 'pa' || arrow.type === 'pb' ||
                        arrow.type === 'ra' || arrow.type === 'rb';
      const dstIsAtom = arrow.type === 'pa' || arrow.type === 'ba' ||
                        arrow.type === 'ra' || arrow.type === 'cba';

      const src = srcIsAtom
        ? atomIdToIndex.get(arrow.srcId)
        : bondIdToIndex.get(arrow.srcId);
      const dst = dstIsAtom
        ? atomIdToIndex.get(arrow.dstId)
        : bondIdToIndex.get(arrow.dstId);

      if (src === undefined || dst === undefined) continue;

      const entry: SnapshotArrow = { type: arrow.type, src, dst };
      if (arrow.anchorId) {
        const anchor = atomIdToIndex.get(arrow.anchorId);
        if (anchor !== undefined) entry.anchor = anchor;
      }
      arrows.push(entry);
    }
    if (arrows.length > 0) snapshot.arrows = arrows;
  }

  // Hydrogen bonds (optional)
  const hBonds = workspace.getHydrogenBonds();
  if (hBonds.length > 0) {
    const hydrogenBonds: SnapshotHydrogenBond[] = [];
    for (const hb of hBonds) {
      const donor = atomIdToIndex.get(hb.donorAtomId);
      const donorParent = atomIdToIndex.get(hb.donorParentAtomId);
      const acceptor = atomIdToIndex.get(hb.acceptorAtomId);
      if (donor === undefined || donorParent === undefined || acceptor === undefined) continue;
      hydrogenBonds.push({ donor, donorParent, acceptor });
    }
    if (hydrogenBonds.length > 0) snapshot.hydrogenBonds = hydrogenBonds;
  }

  // Visibility (sparse — only non-visible entries)
  const visEntries: Record<number, 'h' | 'd'> = {};
  let hasVis = false;
  for (const atomId of atomIds) {
    const flag = workspace.getVisibility(atomId);
    const idx = atomIdToIndex.get(atomId)!;
    if (flag === VisibilityFlag.HiddenLP) {
      visEntries[idx] = 'h';
      hasVis = true;
    } else if (flag === VisibilityFlag.Hidden) {
      visEntries[idx] = 'd';
      hasVis = true;
    }
  }
  if (hasVis) snapshot.visibility = visEntries;

  // Annotations (optional, free-floating — no index references)
  const wsAnnotations = workspace.getAnnotations();
  if (wsAnnotations.length > 0) {
    const annotations: SnapshotAnnotation[] = [];
    for (const ann of wsAnnotations) {
      const entry: SnapshotAnnotation = { type: ann.type, x: ann.x, y: ann.y };
      // Omit defaults for compactness
      if (ann.rotation !== 0) entry.rotation = ann.rotation;
      if (ann.scale !== 1) entry.scale = ann.scale;

      if (ann.type === 'symbol') {
        entry.symbolId = ann.symbolId;
        if (ann.shaftLength !== undefined) entry.shaftLength = ann.shaftLength;
      } else {
        entry.text = ann.text;
        if (ann.fontSize !== undefined) entry.fontSize = ann.fontSize;
        if (ann.alignment !== undefined) entry.alignment = ann.alignment;
      }
      annotations.push(entry);
    }
    if (annotations.length > 0) snapshot.annotations = annotations;
  }

  // Groups (optional — atom IDs converted to indices)
  const wsGroups = workspace.getGroups();
  if (wsGroups.length > 0) {
    const groups: SnapshotGroup[] = [];
    for (const group of wsGroups) {
      const atomIndices: number[] = [];
      for (const atomId of group.atomIds) {
        const idx = atomIdToIndex.get(atomId);
        if (idx !== undefined) atomIndices.push(idx);
      }
      if (atomIndices.length > 0) {
        const entry: SnapshotGroup = { name: group.name, atoms: atomIndices };
        if (group.templateId) entry.templateId = group.templateId;
        groups.push(entry);
      }
    }
    if (groups.length > 0) snapshot.groups = groups;
  }

  // Templates (optional — each template is a self-contained mini-snapshot)
  const wsTemplates = workspace.getTemplates();
  if (wsTemplates.length > 0) {
    const templates: SnapshotTemplate[] = [];
    for (const tpl of wsTemplates) {
      const tplAtoms: SnapshotAtom[] = [];
      const tplAtomIds: string[] = [];
      const tplAtomIdToIndex = new Map<string, number>();
      for (const atom of tpl.workspace.getAllAtoms()) {
        tplAtomIdToIndex.set(atom.id, tplAtoms.length);
        tplAtomIds.push(atom.id);
        const entry: SnapshotAtom = { symbol: atom.symbol, x: atom.x, y: atom.y };
        if (atom.charge !== 0) entry.charge = atom.charge;
        if (atom.lonePairs !== 0) entry.lonePairs = atom.lonePairs;
        if (atom.radicals !== 0) entry.radicals = atom.radicals;
        if (atom.chirality) entry.chirality = atom.chirality;
        tplAtoms.push(entry);
      }

      const tplBonds: SnapshotBond[] = [];
      for (const bond of tpl.workspace.getAllLooseBonds()) {
        const a1 = tplAtomIdToIndex.get(bond.a1.id);
        const a2 = tplAtomIdToIndex.get(bond.a2.id);
        if (a1 === undefined || a2 === undefined) continue;
        const entry: SnapshotBond = { a1, a2, order: bond.order };
        if (bond.bondType !== 'l') entry.type = bond.bondType;
        if (bond.isomerConfig) entry.isomerConfig = bond.isomerConfig;
        tplBonds.push(entry);
      }

      const tplEntry: SnapshotTemplate = { id: tpl.id, name: tpl.name, atoms: tplAtoms, bonds: tplBonds };
      if (tpl.category && tpl.category !== '') tplEntry.category = tpl.category;

      // Template-local visibility
      let hasTplVis = false;
      const tplVis: Record<number, 'h' | 'd'> = {};
      for (const atomId of tplAtomIds) {
        const flag = tpl.workspace.getVisibility(atomId);
        const idx = tplAtomIdToIndex.get(atomId)!;
        if (flag === VisibilityFlag.HiddenLP) { tplVis[idx] = 'h'; hasTplVis = true; }
        else if (flag === VisibilityFlag.Hidden) { tplVis[idx] = 'd'; hasTplVis = true; }
      }
      if (hasTplVis) tplEntry.visibility = tplVis;

      templates.push(tplEntry);
    }
    if (templates.length > 0) snapshot.templates = templates;
  }

  // Group instances (optional — references templates by ID)
  const wsInstances = workspace.getGroupInstances();
  if (wsInstances.length > 0) {
    const instances: SnapshotGroupInstance[] = [];
    for (const inst of wsInstances) {
      const entry: SnapshotGroupInstance = {
        templateId: inst.templateId,
        tx: inst.transform.tx,
        ty: inst.transform.ty,
        rotation: inst.transform.rotation,
      };

      // Serialize sparse overrides using local atom indices
      if (inst.overrides) {
        const tpl = workspace.getTemplate(inst.templateId);
        if (tpl) {
          const localAtomIds = tpl.workspace.getAllAtoms().map(a => a.id);
          const localIdToIndex = new Map(localAtomIds.map((id, i) => [id, i]));
          const ov: SnapshotInstanceOverrides = {};
          let hasOv = false;

          if (inst.overrides.visibility) {
            const vis: Record<number, 'h' | 'd'> = {};
            for (const [localId, flag] of inst.overrides.visibility) {
              const idx = localIdToIndex.get(localId);
              if (idx === undefined) continue;
              if (flag === VisibilityFlag.HiddenLP) { vis[idx] = 'h'; hasOv = true; }
              else if (flag === VisibilityFlag.Hidden) { vis[idx] = 'd'; hasOv = true; }
            }
            if (Object.keys(vis).length > 0) ov.visibility = vis;
          }
          if (inst.overrides.elements) {
            const map: Record<number, string> = {};
            for (const [localId, val] of inst.overrides.elements) {
              const idx = localIdToIndex.get(localId); if (idx !== undefined) { map[idx] = val; hasOv = true; }
            }
            if (Object.keys(map).length > 0) ov.elements = map;
          }
          if (inst.overrides.charges) {
            const map: Record<number, number> = {};
            for (const [localId, val] of inst.overrides.charges) {
              const idx = localIdToIndex.get(localId); if (idx !== undefined) { map[idx] = val; hasOv = true; }
            }
            if (Object.keys(map).length > 0) ov.charges = map;
          }
          if (inst.overrides.lonePairs) {
            const map: Record<number, number> = {};
            for (const [localId, val] of inst.overrides.lonePairs) {
              const idx = localIdToIndex.get(localId); if (idx !== undefined) { map[idx] = val; hasOv = true; }
            }
            if (Object.keys(map).length > 0) ov.lonePairs = map;
          }
          if (inst.overrides.radicals) {
            const map: Record<number, number> = {};
            for (const [localId, val] of inst.overrides.radicals) {
              const idx = localIdToIndex.get(localId); if (idx !== undefined) { map[idx] = val; hasOv = true; }
            }
            if (Object.keys(map).length > 0) ov.radicals = map;
          }

          if (hasOv) entry.overrides = ov;
        }
      }

      instances.push(entry);
    }
    if (instances.length > 0) snapshot.instances = instances;
  }

  return snapshot;
}

/**
 * Reconstruct a MolecularWorkspace from a WorkspaceSnapshotV1.
 */
export function fromSnapshot(snapshot: WorkspaceSnapshotV1): MolecularWorkspace {
  // Build an AtomContainer from snapshot arrays, then use fromAtomContainer
  const container = new AtomContainer();
  const atomModelMap: AtomModel[] = [];

  for (const sa of snapshot.atoms) {
    const am = new AtomModel(sa.symbol);
    am.id = `a${stableId()}`;
    am.charge = sa.charge ?? 0;
    am.lonePairs = sa.lonePairs ?? 0;
    am.radicals = sa.radicals ?? 0;
    am.pos = { x: sa.x, y: sa.y };
    // PIPH-269: round-trip R/S stereochemistry through the snapshot.
    if (sa.chirality === 'r') am.chirality = AtomChirality.R;
    else if (sa.chirality === 's') am.chirality = AtomChirality.S;
    container.atoms.push(am);
    atomModelMap.push(am);
  }

  for (const sb of snapshot.bonds) {
    const a1 = atomModelMap[sb.a1];
    const a2 = atomModelMap[sb.a2];
    if (!a1 || !a2) continue;
    const bm = new BondModel(a1, a2, sb.order, sb.type ?? 'l');
    bm.id = `b${stableId()}`;
    // PIPH-269: round-trip E/Z stereochemistry through the snapshot.
    if (sb.isomerConfig) bm.isomerConfig = sb.isomerConfig;
    container.bonds.push(bm);
  }

  let ws = fromAtomContainer(container);
  const atomIds = ws.getAtomIds();
  const bondIds = ws.getBondIds();

  // Apply visibility
  if (snapshot.visibility) {
    for (const [idxStr, flag] of Object.entries(snapshot.visibility)) {
      const idx = parseInt(idxStr, 10);
      const atomId = atomIds[idx];
      if (!atomId) continue;
      ws = ws.setVisibility(
        atomId,
        flag === 'h' ? VisibilityFlag.HiddenLP : VisibilityFlag.Hidden,
      );
    }
  }

  // Apply arrows
  if (snapshot.arrows && snapshot.arrows.length > 0) {
    const arrowIdDefs: ArrowIdDef[] = [];
    for (const sa of snapshot.arrows) {
      const srcIsAtom = sa.type === 'pa' || sa.type === 'pb' ||
                        sa.type === 'ra' || sa.type === 'rb';
      const dstIsAtom = sa.type === 'pa' || sa.type === 'ba' ||
                        sa.type === 'ra' || sa.type === 'cba';

      const srcId = srcIsAtom ? atomIds[sa.src] : bondIds[sa.src];
      const dstId = dstIsAtom ? atomIds[sa.dst] : bondIds[sa.dst];
      if (!srcId || !dstId) continue;

      const idArrow: ArrowIdDef = { type: sa.type as any, srcId, dstId };
      if (sa.anchor !== undefined) {
        const anchorId = atomIds[sa.anchor];
        if (anchorId) idArrow.anchorId = anchorId;
      }
      arrowIdDefs.push(idArrow);
    }
    ws = ws.setArrows(arrowIdDefs);
  }

  // Apply hydrogen bonds
  if (snapshot.hydrogenBonds && snapshot.hydrogenBonds.length > 0) {
    for (const shb of snapshot.hydrogenBonds) {
      const donorAtomId = atomIds[shb.donor];
      const donorParentAtomId = atomIds[shb.donorParent];
      const acceptorAtomId = atomIds[shb.acceptor];
      if (donorAtomId && donorParentAtomId && acceptorAtomId) {
        ws = ws.addHydrogenBond({ donorAtomId, donorParentAtomId, acceptorAtomId });
      }
    }
  }

  // Apply annotations (free-floating — no index resolution needed)
  if (snapshot.annotations && snapshot.annotations.length > 0) {
    for (const sa of snapshot.annotations) {
      if (sa.type === 'symbol' && sa.symbolId) {
        const result = ws.addAnnotation({
          type: 'symbol',
          x: sa.x,
          y: sa.y,
          rotation: sa.rotation ?? 0,
          scale: sa.scale ?? 1,
          symbolId: sa.symbolId,
          shaftLength: sa.shaftLength,
        });
        ws = result.workspace;
      } else if (sa.type === 'text' && sa.text) {
        const result = ws.addAnnotation({
          type: 'text',
          x: sa.x,
          y: sa.y,
          rotation: sa.rotation ?? 0,
          scale: sa.scale ?? 1,
          text: sa.text,
          fontSize: sa.fontSize,
          alignment: sa.alignment,
        });
        ws = result.workspace;
      }
    }
  }

  // Apply groups (atom indices → IDs)
  if (snapshot.groups && snapshot.groups.length > 0) {
    for (const sg of snapshot.groups) {
      const groupAtomIds: string[] = [];
      for (const idx of sg.atoms) {
        const atomId = atomIds[idx];
        if (atomId) groupAtomIds.push(atomId);
      }
      if (groupAtomIds.length > 0) {
        const result = ws.addGroup({
          name: sg.name,
          templateId: sg.templateId,
          atomIds: groupAtomIds,
        });
        ws = result.workspace;
      }
    }
  }

  // Rebuild templates from snapshot
  if (snapshot.templates && snapshot.templates.length > 0) {
    for (const st of snapshot.templates) {
      // Build the template's workspace from its inline atoms/bonds
      let tplWs = new MolecularWorkspace();
      const tplAtomIds: string[] = [];

      for (const sa of st.atoms) {
        const result = tplWs.addAtom({
          symbol: sa.symbol, x: sa.x, y: sa.y,
          charge: sa.charge ?? 0, lonePairs: sa.lonePairs ?? 0, radicals: sa.radicals ?? 0,
          // PIPH-269: round-trip R/S in template atoms.
          ...(sa.chirality && { chirality: sa.chirality }),
        });
        tplWs = result.workspace;
        tplAtomIds.push(result.atomId);
      }

      for (const sb of st.bonds) {
        const a1Id = tplAtomIds[sb.a1];
        const a2Id = tplAtomIds[sb.a2];
        if (a1Id && a2Id) {
          tplWs = tplWs.addBond({
            order: sb.order,
            bondType: sb.type ?? 'l',
            // PIPH-269: round-trip E/Z in template bonds.
            ...(sb.isomerConfig && { isomerConfig: sb.isomerConfig }),
          }, a1Id, a2Id).workspace;
        }
      }

      if (st.visibility) {
        for (const [idxStr, flag] of Object.entries(st.visibility)) {
          const atomId = tplAtomIds[parseInt(idxStr, 10)];
          if (atomId) {
            tplWs = tplWs.setVisibility(
              atomId,
              flag === 'h' ? VisibilityFlag.HiddenLP : VisibilityFlag.Hidden,
            );
          }
        }
      }

      ws = ws.addTemplate({ id: st.id, name: st.name, category: st.category ?? '', workspace: tplWs });
    }
  }

  // Rebuild group instances from snapshot
  if (snapshot.instances && snapshot.instances.length > 0) {
    for (const si of snapshot.instances) {
      const transform = { tx: si.tx, ty: si.ty, rotation: si.rotation };

      // Rebuild overrides: index-based → local atom ID-based
      let overrides: import('@alchemiesolns/molecular-workspace').InstanceOverrides | undefined;
      if (si.overrides) {
        const tpl = ws.getTemplate(si.templateId);
        if (tpl) {
          const localAtomIds = tpl.workspace.getAllAtoms().map(a => a.id);
          const rebuildMap = <T,>(rec: Record<number, T> | undefined): Map<string, T> | undefined => {
            if (!rec) return undefined;
            const m = new Map<string, T>();
            for (const [idxStr, val] of Object.entries(rec)) {
              const id = localAtomIds[parseInt(idxStr, 10)];
              if (id) m.set(id, val);
            }
            return m.size > 0 ? m : undefined;
          };

          const visMap = si.overrides.visibility
            ? new Map(Object.entries(si.overrides.visibility).map(([idxStr, flag]) => {
                const id = localAtomIds[parseInt(idxStr, 10)] ?? '';
                return [id, flag === 'h' ? VisibilityFlag.HiddenLP : VisibilityFlag.Hidden] as const;
              }).filter(([id]) => id !== ''))
            : undefined;

          overrides = {
            visibility: visMap && visMap.size > 0 ? visMap : undefined,
            elements: rebuildMap(si.overrides.elements),
            charges: rebuildMap(si.overrides.charges),
            lonePairs: rebuildMap(si.overrides.lonePairs),
            radicals: rebuildMap(si.overrides.radicals),
          };
          // Clean up: if all fields are undefined, don't set overrides
          if (!overrides.visibility && !overrides.elements && !overrides.charges &&
              !overrides.lonePairs && !overrides.radicals) {
            overrides = undefined;
          }
        }
      }

      ws = ws.addGroupInstance(si.templateId, transform, overrides).workspace;
    }
  }

  return ws;
}
